import { useEffect, useState } from "react";
import { api, currency } from "@/lib/api";
import { Badge } from "@/components/ui/badge";

const SOURCE = {
  recorded_at_sale: "Recorded at sale",
  current_product: "Current classification",
  unavailable: "Not available",
};

function Field({ label, value, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="text-sm break-words" data-testid={testid}>{value ?? "—"}</div>
    </div>
  );
}

function LineCard({ l, i }) {
  const current = l.category_source !== "recorded_at_sale";
  return (
    <div className="rounded-md border p-3 space-y-2" data-testid={`txn-line-${i}`}>
      <div className="flex justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-medium" data-testid={`txn-line-name-${i}`}>{l.name}</div>
          <div className="font-mono text-[11px] text-muted-foreground" data-testid={`txn-line-sku-${i}`}>{l.sku}</div>
        </div>
        <div className="font-mono text-sm shrink-0" data-testid={`txn-line-total-${i}`}>{currency(l.line_total)}</div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Field label="Category" value={l.category || "—"} testid={`txn-line-category-${i}`} />
        <Field label="Subcategory" value={l.subcategory || "Not classified"} testid={`txn-line-subcategory-${i}`} />
        <Field label="Qty / unit" value={`${l.quantity} ${l.unit || ""}`.trim()} testid={`txn-line-qty-${i}`} />
        <Field label="Unit price" value={currency(l.unit_price)} testid={`txn-line-price-${i}`} />
        <Field label="Line discount" value={l.line_discount > 0 ? `− ${currency(l.line_discount)}${l.special_deal ? ` (${l.special_deal})` : ""}` : "None"} testid={`txn-line-discount-${i}`} />
        {l.classifications.length > 0 && <Field label="Other classification" value={l.classifications.join(" › ")} />}
      </div>
      <Badge variant="outline" data-testid={`txn-line-class-source-${i}`}
        className={current ? "text-[10px] border-amber-300 text-amber-800 bg-amber-50" : "text-[10px] border-emerald-300 text-emerald-800 bg-emerald-50"}>
        {SOURCE[l.category_source]}{current && l.category_source === "current_product" ? " — not recorded at time of sale" : ""}
      </Badge>
    </div>
  );
}

export default function SaleTransactionDetail({ saleId }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    setD(null); setErr(null);
    api.get(`/sales/${saleId}/detail`).then((r) => setD(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Could not load transaction"));
  }, [saleId]);
  if (err) return <div className="text-sm text-red-700" data-testid="txn-detail-error">{err}</div>;
  if (!d) return <div className="text-sm text-muted-foreground">Loading transaction…</div>;
  const at = new Date(d.at);
  return (
    <div className="space-y-4" data-testid="txn-detail">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Transaction ID" value={<span className="font-mono text-xs">{d.receipt_no}<br />{d.id}</span>} testid="txn-id" />
        <Field label="Date & time" value={at.toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "medium" })} testid="txn-datetime" />
        <Field label="Store" value={d.store_name} testid="txn-store" />
        <Field label="Cashier" value={d.cashier_name} testid="txn-cashier" />
        <Field label="Payment" value={d.payment_method === "Split" && d.payment_breakdown
          ? `Split — Cash ${currency(d.payment_breakdown.cash)} / Card ${currency(d.payment_breakdown.card)}` : d.payment_method} testid="txn-payment" />
        <Field label="Status" value={d.status} testid="txn-status" />
      </div>
      <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{d.lines.length} product line{d.lines.length === 1 ? "" : "s"}</div>
      <div className="space-y-2">{d.lines.map((l, i) => <LineCard key={i} l={l} i={i} />)}</div>
      <div className="rounded-md bg-muted/40 p-3 space-y-1 text-sm font-mono">
        <div className="flex justify-between"><span>Lines subtotal</span><span>{currency(d.subtotal)}</span></div>
        {(Number(d.deal_discount_amount) > 0 || Number(d.manual_discount_amount) > 0) && (
          <div className="text-xs text-muted-foreground font-sans">Transaction-level discounts are itemised below.</div>
        )}
        <div className="flex justify-between font-semibold font-display text-lg pt-1"><span>Total</span><span data-testid="txn-total">{currency(d.total)}</span></div>
      </div>
    </div>
  );
}
